import { FC, useState } from 'react';
import { Stack, Typography, useTheme } from '@mui/material';
import { CopyToClipboard } from '../CopyToClipboard';
import { Chip, ContainedButton, Paper, TextButton } from '@shared/components';
import { ChevronLeft } from '@mui/icons-material';
import Collapse from '@mui/material/Collapse';
import { useAppTFunction } from '@retail/app/i18n';
import { opsFace } from '@assets/images';
import { getLLDate } from '@retail/utils';

export interface SentryErrorProps {
  componentStack?: string;
  message?: string;
  eventId?: string;
  resetError?: () => void;
}

export const ErrorFallback: FC<SentryErrorProps> = ({
  componentStack,
  message,
  eventId,
  resetError,
}) => {
  const t = useAppTFunction();
  const theme = useTheme();
  const { tint } = theme.vars.palette.semantic;
  const [showComponentStack, setShowComponentStack] = useState(false);
  const errorDate = new Date();

  return (
    <Stack alignSelf="center" alignItems="center" spacing={3}>
      <img src={opsFace} width="100px" height="100px" alt="" />
      <Typography variant="h5">{t('app.error.oops')}</Typography>
      <Typography variant="body1">{t('app.error.generic')}</Typography>
      <Typography variant="body1" maxWidth="380px" textAlign="center" color="textSecondary">
        {t('app.error.description')}
      </Typography>
      {!!eventId && (
        <Stack direction="row" spacing={2} alignItems="center" data-chromatic="ignore">
          <Typography variant="body2">{`${t('app.error.errorCode')}:`}</Typography>
          <CopyToClipboard value={eventId}>
            <Chip
              backgroundColor={tint.sky.medium}
              textColor={tint.sky.text}
              label={<Typography variant="body2">{eventId}</Typography>}
            />
          </CopyToClipboard>
        </Stack>
      )}
      {!!resetError && (
        <ContainedButton onClick={resetError}>{t('app.error.retry')}</ContainedButton>
      )}
      <Stack display="flex" flexDirection="row" alignItems="center" gap={1}>
        <Typography variant="subtitle2">{t('common.errorOccured')}:</Typography>
        <Typography variant="body2">{`${getLLDate(errorDate)}, ${errorDate.toLocaleTimeString(
          'no-NO'
        )}`}</Typography>
      </Stack>
      {componentStack && (
        <>
          <TextButton
            size="small"
            onClick={() => setShowComponentStack((prevState) => !prevState)}
            endIcon={
              <ChevronLeft
                sx={{
                  transform: showComponentStack ? 'rotate(90deg)' : 'rotate(-90deg)',
                  transition: 'transform 0.2s ease-in-out',
                }}
              />
            }
          >
            {t('app.error.showMore')}
          </TextButton>

          <Collapse in={showComponentStack}>
            <Paper sx={{ py: 3, px: 4, backgroundColor: 'background.secondary' }}>
              <Typography>{message}</Typography>
              {componentStack}
            </Paper>
          </Collapse>
        </>
      )}
    </Stack>
  );
};
